import {
  AfterViewInit,
  Component,
  ElementRef,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RUTA_INICIO } from '../../guards/auth.guard';
import { AuthService, IResultadoAcceso } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';

/** Forma mínima de un correo. No valida que exista: valida que se pueda leer. */
const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Pantalla de acceso.
 *
 * Vive fuera de `DashboardLayoutComponent`: aquí no hay panel lateral ni barra
 * superior, sólo una tarjeta de cristal sobre las auroras que ya pinta el
 * `<body>`. Por eso el interruptor de tema se repite aquí — es el único sitio
 * desde donde se puede cambiar antes de entrar.
 *
 * Toda la lógica de credenciales, sesión y almacenamiento es de `AuthService`;
 * este componente sólo valida la forma de lo tecleado, pinta los estados y
 * navega. Ver `docs/ACCESO.md`.
 */
@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
  standalone: false,
})
export class LoginPageComponent implements AfterViewInit {

  /* ---- lo tecleado ---- */

  public correo: string = '';
  public contrasena: string = '';
  public recordar: boolean = false;

  /* ---- estado de la pantalla ---- */

  public readonly enviando: WritableSignal<boolean> = signal<boolean>(false);
  public readonly verContrasena: WritableSignal<boolean> = signal<boolean>(false);
  public readonly mayusculas: WritableSignal<boolean> = signal<boolean>(false);

  /** Error de credenciales. Llega tal cual de `AuthService`, no se reescribe. */
  public readonly errorAcceso: WritableSignal<string> = signal<string>('');

  public readonly errorCorreo: WritableSignal<string> = signal<string>('');
  public readonly errorContrasena: WritableSignal<string> = signal<string>('');

  public readonly themeService: ThemeService = inject(ThemeService);

  /**
   * Cada campo describe SÓLO su propio mensaje; el error de credenciales sí se
   * enlaza desde los dos, porque afecta a los dos. Con los dos campos apuntando
   * al mismo error, al enfocar el correo el lector anunciaba el problema de la
   * contraseña.
   */
  public readonly describeCorreo: Signal<string | null> = computed<string | null>(() =>
    this.describir(!!this.errorCorreo(), 'acceso-correo-error'));

  public readonly describeContrasena: Signal<string | null> = computed<string | null>(() => {
    const partes: Array<string> = [];

    if (this.errorContrasena()) {
      partes.push('acceso-contrasena-error');
    }

    if (this.mayusculas()) {
      partes.push('acceso-mayusculas');
    }

    if (this.errorAcceso()) {
      partes.push('acceso-error');
    }

    return partes.length ? partes.join(' ') : null;
  });

  @ViewChild('aviso') private avisoRef?: ElementRef<HTMLElement>;
  @ViewChild('campoCorreo') private correoRef?: ElementRef<HTMLInputElement>;
  @ViewChild('campoContrasena') private contrasenaRef?: ElementRef<HTMLInputElement>;

  private readonly authService: AuthService = inject(AuthService);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);

  /**
   * Cuenta de envíos. Durante los 520ms que tarda `login()` los campos siguen
   * activos, así que el usuario puede corregir la contraseña o pulsar «Usar la
   * cuenta de demostración» antes de que llegue la respuesta. Sin este número,
   * esa respuesta vieja pinta en rojo unas credenciales que ya son otras.
   */
  private envio: number = 0;

  /**
   * Foco inicial sólo con ratón. En un teléfono, enfocar al arrancar levanta el
   * teclado y descuadra la tarjeta antes de que el usuario haya visto nada.
   */
  public ngAfterViewInit(): void {
    if (matchMedia('(pointer: fine)').matches) {
      this.correoRef?.nativeElement.focus();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR ENTRAR                               */
  /* -------------------------------------------------------------------------- */

  public async entrar(): Promise<void> {
    // El botón no se deshabilita (perdería el foco y se pintaría al 50%), así
    // que el doble envío lo corta esta guarda.
    if (this.enviando()) {
      return;
    }

    if (!this.validar()) {
      return;
    }

    this.enviando.set(true);
    this.errorAcceso.set('');

    const envio: number = ++this.envio;

    const resultado: IResultadoAcceso =
      await this.authService.login(this.correo, this.contrasena, this.recordar);

    // Lo tecleado cambió mientras la promesa volaba: esta respuesta ya no habla
    // de lo que hay en pantalla y aplicarla sería acusar al texto equivocado.
    if (envio !== this.envio) {
      this.enviando.set(false);

      return;
    }

    if (resultado.ok) {
      // `enviando` se queda en true hasta que cambia la vista: si volviera a
      // «Entrar» se vería un parpadeo justo antes del salto.
      this.verContrasena.set(false);
      this.router.navigateByUrl(this.destino(), { replaceUrl: true });

      return;
    }

    this.enviando.set(false);
    this.errorAcceso.set(resultado.mensaje);

    // Al ciclo siguiente: el aviso todavía no está en el DOM. El foco va al
    // contenedor, que CONTIENE el mensaje, y el lector lo anuncia al enfocarlo;
    // una región viva y un salto de foco a la vez se pisan.
    setTimeout(() => {
      const aviso: HTMLElement | undefined = this.avisoRef?.nativeElement;

      if (!aviso) {
        return;
      }

      aviso.focus();
      // `behavior` explícito gana sobre el `scroll-behavior: auto !important`
      // del bloque global de movimiento reducido: sólo cede cuando vale 'auto'.
      aviso.scrollIntoView({ block: 'center', behavior: this.comportamientoScroll() });
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR FORMULARIO                             */
  /* -------------------------------------------------------------------------- */

  public alternarContrasena(): void {
    this.verContrasena.update((visible: boolean) => !visible);
  }

  /**
   * Bloq Mayús es la causa número uno de fallo de acceso legítimo, y aquí pesa
   * el doble: el error es genérico y la contraseña está impresa más abajo.
   *
   * El aviso NO se apaga al perder el foco: el camino de fallo mueve el foco al
   * aviso de error, así que apagarlo ahí borraría la pista justo en el
   * fotograma en que aparece el error que explica. La siguiente tecla lo
   * corrige, y mientras tanto sigue diciendo la verdad.
   */
  public revisarMayusculas(event: KeyboardEvent): void {
    this.mayusculas.set(event.getModifierState('CapsLock'));
  }

  /** Corregir es la señal de que el mensaje ya se entendió: se retira mientras
   *  se teclea en vez de regañar dos veces. Y descarta el envío en vuelo: su
   *  respuesta hablaría de un texto que ya no está en pantalla. */
  public limpiarCorreo(): void {
    this.envio++;
    this.errorCorreo.set('');
    this.errorAcceso.set('');
  }

  public limpiarContrasena(): void {
    this.envio++;
    this.errorContrasena.set('');
    this.errorAcceso.set('');
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR INTERNO                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Validación propia y no la nativa: con `novalidate` no salen los globos del
   * navegador, que vienen en el idioma del sistema y con acabado ajeno.
   */
  private validar(): boolean {
    const correo: string = this.correo.trim();

    this.errorCorreo.set('');
    this.errorContrasena.set('');

    if (!correo) {
      this.errorCorreo.set('Escribe tu correo electrónico.');
    } else if (!CORREO.test(correo)) {
      this.errorCorreo.set('El correo debe tener el formato nombre@empresa.com.');
    }

    if (!this.contrasena) {
      this.errorContrasena.set('Escribe tu contraseña.');
    }

    if (!this.errorCorreo() && !this.errorContrasena()) {
      return true;
    }

    // Los errores de forma y el de credenciales son excluyentes: dos avisos a
    // la vez sobre el mismo intento se contradicen.
    this.errorAcceso.set('');

    // Al ciclo siguiente: si el foco llega antes que el mensaje, el lector lee
    // el campo sin su descripción.
    setTimeout(() => {
      const destino: ElementRef<HTMLInputElement> | undefined =
        this.errorCorreo() ? this.correoRef : this.contrasenaRef;

      destino?.nativeElement.focus();
    });

    return false;
  }

  /**
   * El bloque global de `prefers-reduced-motion` no puede desactivar un
   * `behavior: 'smooth'` escrito a mano: la opción del método sólo cede a la
   * propiedad CSS cuando vale `'auto'`. Se resuelve en tiempo de llamada, igual
   * que el foco inicial resuelve `(pointer: fine)`.
   */
  private comportamientoScroll(): ScrollBehavior {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** Sólo rutas internas: `regresarA` viaja en la URL y es texto de fuera. */
  private destino(): string {
    const pedido: string | null = this.route.snapshot.queryParamMap.get('regresarA');
    const interno: boolean = !!pedido && pedido.startsWith('/') && !pedido.startsWith('//');

    return interno ? pedido as string : RUTA_INICIO;
  }

  private describir(activo: boolean, id: string): string | null {
    const partes: Array<string> = [];

    if (activo) {
      partes.push(id);
    }

    if (this.errorAcceso()) {
      partes.push('acceso-error');
    }

    return partes.length ? partes.join(' ') : null;
  }
}
